import { useState } from "react";
import type { Track } from "@vms/types";
import { TrackPicker, type TrackCoverageMap } from "./TrackPicker.js";

interface TrackMultiSelectProps {
  tracks: Track[] | null | undefined;
  /** Selected entries as `trackId` or `trackId/layoutId` strings. */
  value: string[];
  onChange: (value: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
  coverage?: TrackCoverageMap;
}

/**
 * Chip-list + add-picker for tracks. Composes {@link TrackPicker} for the
 * "add another" affordance. Mirrors the inline implementation that lived in
 * hub-ui's `LeaderboardManagementForm`.
 */
export function TrackMultiSelect({
  tracks,
  value,
  onChange,
  loading,
  disabled,
  coverage,
}: TrackMultiSelectProps) {
  const [pending, setPending] = useState<string>("");

  function handlePicked(picked: string) {
    if (picked && !value.includes(picked)) {
      onChange([...value, picked]);
    }
    setPending("");
  }

  function removeValue(v: string) {
    onChange(value.filter((x) => x !== v));
  }

  function coverageHintFor(trackId: string): string | null {
    if (!coverage) return null;
    const entry = coverage.byTrackId[trackId];
    if (!entry) return null;
    if (entry.availableInVenues.length >= coverage.totalVenues) return null;
    return `In ${entry.availableInVenues.length} of ${coverage.totalVenues} venues`;
  }

  return (
    <div>
      <div className="space-y-2 mb-2">
        {value.map((entry) => {
          const [trackId, layoutId] = entry.split("/");
          const track = tracks?.find((tr) => tr.trackId === trackId);
          const layout = layoutId ? track?.layouts?.find((l) => l.layoutId === layoutId) : null;
          const outlineUrl = layout?.outlineUrl ?? track?.outlineUrl;
          const hint = coverageHintFor(trackId);
          return (
            <div
              key={entry}
              className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white"
            >
              {outlineUrl ? (
                <div className="w-10 h-10 bg-gray-800 rounded flex-shrink-0 flex items-center justify-center p-0.5">
                  <img src={outlineUrl} alt="" className="max-w-full max-h-full object-contain" />
                </div>
              ) : track?.previewUrl ? (
                <img src={track.previewUrl} alt="" className="w-10 h-10 object-cover rounded flex-shrink-0" />
              ) : null}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{track?.name ?? trackId}</p>
                {layout && <p className="text-xs text-gray-500 truncate">{layout.name}</p>}
                {hint && <p className="text-xs text-amber-600 mt-0.5">{hint}</p>}
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeValue(entry)}
                  className="text-gray-400 hover:text-red-500 flex-shrink-0"
                  aria-label="Remove track"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          );
        })}
      </div>
      <TrackPicker
        tracks={tracks}
        value={pending}
        onChange={handlePicked}
        loading={loading}
        disabled={disabled}
        coverage={coverage}
      />
    </div>
  );
}
